import { useMemo, useState } from "react"
import type { ReactNode } from "react"
import { LoadState } from "@/components/ui/LoadState"

export type ContentAssetOption = {
  id: string
  label: string
  sublabel?: string
  badge?: { label: string; tone?: "neutral" | "success" | "warning" }
}

type ContentAssetPickerProps = {
  label: string
  helpText?: string
  items: ContentAssetOption[]
  selectedId: string | null
  onSelect: (id: string) => void
  loading?: boolean
  disabled?: boolean
  saving?: boolean
  emptyMessage?: string
  /** Icon rendered inside every row's swatch (same icon for all rows of this picker). */
  icon: ReactNode
  /** Omitted when the association can't be cleared (e.g. a quiz always belongs to some lesson). */
  allowNone?: boolean
  noneLabel?: string
  /** Text after the item count badge, e.g. "en este curso" or "disponibles". */
  countLabel?: string
}

const BADGE_TONE_CLS: Record<NonNullable<ContentAssetOption["badge"]>["tone"] & string, string> = {
  neutral: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
  success: "bg-[color:rgba(34,197,94,0.14)] text-success-700 dark:text-success-300",
  warning: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
}

// Above this count a plain scroll list stops being scannable — show a
// search box and cap the visible rows instead of dumping all of them.
const SEARCH_THRESHOLD = 6
const VISIBLE_LIMIT = 8

function matches(option: ContentAssetOption, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return option.label.toLowerCase().includes(q) || (option.sublabel?.toLowerCase().includes(q) ?? false)
}

function IconSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

function AssetRow({
  option,
  icon,
  selected,
  onSelect,
  disabled,
}: {
  option: ContentAssetOption
  icon: ReactNode
  selected: boolean
  onSelect: () => void
  disabled: boolean
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors ${
        disabled ? "cursor-not-allowed opacity-50" : ""
      } ${
        selected
          ? "border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-brand-500/10"
          : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/60"
      }`}
    >
      <input
        type="radio"
        checked={selected}
        disabled={disabled}
        onChange={onSelect}
        className="sr-only"
      />
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
          selected ? "border-brand-500" : "border-gray-300 dark:border-gray-600"
        }`}
      >
        {selected && <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />}
      </span>
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${
          selected
            ? "border-brand-100 bg-white text-brand-600 dark:border-brand-500/30 dark:bg-gray-900"
            : "border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800"
        }`}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-gray-800 dark:text-white">{option.label}</span>
        {option.sublabel && <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{option.sublabel}</span>}
      </span>
      {option.badge && (
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${BADGE_TONE_CLS[option.badge.tone ?? "neutral"]}`}>
          {option.badge.label}
        </span>
      )}
    </label>
  )
}

/**
 * Course-scoped "associate an existing X" picker (lesson-content-association):
 * video/PDF/quiz are uploaded or built once, independent of any lesson, then
 * picked here as a scannable card list rather than authored inline or buried
 * in a plain <select>.
 */
export function ContentAssetPicker({
  label,
  helpText,
  items,
  selectedId,
  onSelect,
  loading = false,
  disabled = false,
  saving = false,
  emptyMessage = "No hay nada disponible en este curso todavía.",
  icon,
  allowNone = true,
  noneLabel = "Sin asociar",
  countLabel = "en este curso",
}: ContentAssetPickerProps) {
  const isDisabled = disabled || saving
  const [query, setQuery] = useState("")
  const showSearch = items.length > SEARCH_THRESHOLD

  const selectedItem = items.find((i) => i.id === selectedId) ?? null
  const filtered = useMemo(() => items.filter((i) => matches(i, query)), [items, query])
  // Keep the current selection visible even while a search narrows the list —
  // losing sight of what's selected while typing is the whole reason a plain
  // <select> stopped scaling past a handful of items.
  const selectedPinned = selectedItem && query.trim() && !filtered.some((i) => i.id === selectedId) ? selectedItem : null
  const visible = filtered.slice(0, VISIBLE_LIMIT)
  const hiddenCount = filtered.length - visible.length

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
        {items.length > 0 && (
          <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
            {items.length} {countLabel}
          </span>
        )}
      </div>

      {loading ? (
        <LoadState compact />
      ) : items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-200 px-3.5 py-3 text-xs text-gray-500 dark:text-gray-400 dark:border-gray-700">
          {emptyMessage}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {showSearch && (
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">
                <IconSearch />
              </span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nombre..."
                className="h-9 w-full rounded-lg border border-gray-300 bg-white pl-8 pr-3 text-sm text-gray-800 focus:border-[var(--alas-blue)] focus:outline-none focus:ring-2 focus:ring-[var(--alas-blue)]/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
              />
            </div>
          )}

          {allowNone && (
            <AssetRow
              option={{ id: "", label: noneLabel }}
              icon={
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              }
              selected={!selectedId}
              onSelect={() => onSelect("")}
              disabled={isDisabled}
            />
          )}

          {selectedPinned && (
            <AssetRow
              option={selectedPinned}
              icon={icon}
              selected
              onSelect={() => onSelect(selectedPinned.id)}
              disabled={isDisabled}
            />
          )}

          {visible.length === 0 ? (
            <p className="px-1 py-2 text-xs text-gray-500 dark:text-gray-400">Sin resultados para "{query}".</p>
          ) : (
            visible.map((item) => (
              <AssetRow
                key={item.id}
                option={item}
                icon={icon}
                selected={selectedId === item.id}
                onSelect={() => onSelect(item.id)}
                disabled={isDisabled}
              />
            ))
          )}

          {hiddenCount > 0 && (
            <p className="px-1 text-xs text-gray-500 dark:text-gray-400">
              +{hiddenCount} más — refina la búsqueda para verlos.
            </p>
          )}
        </div>
      )}
      {helpText && <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{helpText}</p>}
    </div>
  )
}
